/**
 * @file table-view.tsx
 * @description Утилиты для создания ячеек таблиц в Task Manager приложении. Предоставляет набор функций для создания специализированных ячеек различных типов: стандартные, даты/время, приоритеты, состояния задач, прогресс-бары, контуры и бэджи с автоматической обработкой различных типов данных и стилизации элементов согласно дизайн-системе.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-09-11
 * @dependencies ['classnames', 'moment', '@src/types', '@components/ui/indicators/state-badge', '@components/icons/task-state']
 * @related-files ['src/components/ui/tables', 'src/pages', 'src/types/mes-object.ts', 'src/utils/form-object-helper.ts']
 */

import cn from 'classnames';
import moment from 'moment';
import { TBaseObjectDto } from '@src/types';
import { StateBadge } from '@components/ui/indicators/state-badge';
import {
    ProgressTaskStateIcon,
    SuccessTaskStateIcon,
    WaitingTaskStateIcon,
    NewTaskStateIcon
} from '@components/icons/task-state';
import type { TGanttStateConfigMap, TGanttTaskState } from '@src/types/gantt';

/**
 * Создает ячейку таблицы по умолчанию с автоматическим определением формата
 * @param data - данные для отображения
 * @param className - CSS класс для стилизации (по умолчанию 'wrapper')
 * @returns React элемент ячейки таблицы
 */
export const getDefaultCell = (data: any, className: string = 'wrapper') => {
    if (!data) {
        return '';
    }

    if (['string', 'number'].includes(typeof data)) {
        return (
            <span title={data} className={className}>
                {data}
            </span>
        );
    }

    if (Array.isArray(data)) {
        return <span className={className}>Массив с данными</span>;
    }

    return (
        <span title={data.name} className={className}>
            {data.name}
        </span>
    );
};

/**
 * Создает ячейку с отформатированной датой/временем
 * @param timestamp - временная метка в миллисекундах
 * @param format - формат отображения даты (по умолчанию 'DD.MM.YYYY')
 * @returns React элемент ячейки с датой или пустую строку
 */
export const getDateTimeCell = (timestamp: number, format: string = 'DD.MM.YYYY') => {
    if (timestamp <= 0 || !timestamp) {
        return '';
    }

    return <span className="capitalize">{moment(timestamp).format(format)}</span>;
};

/**
 * Создает ячейку с индикатором приоритета
 * @param priority - объект приоритета или строка
 * @returns React элемент ячейки с визуальным индикатором приоритета
 */
export const getPriorityCell = (priority: TBaseObjectDto | string | null) => {
    if (!priority || typeof priority === 'string') {
        return priority || '';
    }

    return (
        <div
            title={priority?.name || ''}
            className={cn('h-[1.0585vmin] aspect-square rounded-full', getPriorityBgColorByType(priority))}
        />
    );
};

/**
 * Создает составную ячейку из нескольких элементов
 * @param cells - массив ячеек для объединения
 * @returns React элемент с набором ячеек, разделенных визуально
 */
export const getDoubleCell = (...cells: any[]) => {
    return (
        <div className="wrapper flex items-center w-full justify-center">
            {cells.map((cell: any, key: number) => (
                <div
                    key={key}
                    className={cn('wrapper h-full p-0', cell.className, {
                        'border-r border-r-[#00426947]': key < cells.length - 1,
                        'px-[0.6351vmin]': key > 0 && key !== cells.length - 1,
                        'pr-[0.6351vmin]': key === 0,
                        'pl-[0.6351vmin]': key === cells.length - 1
                    })}
                >
                    {cell.value}
                </div>
            ))}
        </div>
    );
};

/**
 * Создает ячейку с отображением контура
 * @param contour - объект контура
 * @returns React элемент ячейки с стилизованным контуром
 */
export const getContourCell = (contour: TBaseObjectDto) => {
    return (
        <div className="h-[1.9054vmin] text-white px-[0.6351vmin] flex items-center bg-[#707070] border-solid border-[0.1059vmin] border-[#B5B5B5] rounded-[0.4234vmin]">
            <span className="text-[1.2703vmin]">{contour?.name}</span>
        </div>
    );
};

/**
 * Создает ячейку с бэджем состояния
 * @param badge - объект состояния
 * @returns React элемент ячейки с бэджем
 */
export const getBadgeCell = (badge: TBaseObjectDto) => {
    return <StateBadge state={badge} className="h-[1.9054vmin]" />;
};

/**
 * Конфигурация состояний для диаграммы Ганта
 */
const GANTT_STATE_CONFIG: TGanttStateConfigMap = {
    'К выполнению': { icon: WaitingTaskStateIcon, color: undefined },
    'Выполняется': { icon: ProgressTaskStateIcon, color: '#e83400' },
    'К тестированию': { icon: ProgressTaskStateIcon, color: '#2697ff' },
    'На тестировании': { icon: ProgressTaskStateIcon, color: '#2697ff' },
    'К приемке': { icon: ProgressTaskStateIcon, color: undefined },
    'Выполнено': { icon: SuccessTaskStateIcon, color: undefined },
    'Новая': { icon: NewTaskStateIcon, color: undefined }
};

/**
 * Возвращает иконку состояния для диаграммы Ганта
 * @param state - название состояния
 * @returns React элемент с иконкой состояния или null
 */
export const getGanttStateIcon = (state: string) => {
    if (!state) {
        return null;
    }

    const config = GANTT_STATE_CONFIG[state as TGanttTaskState];
    if (!config) {
        return null;
    }

    const { icon: IconComponent, color } = config;
    return <IconComponent color={color} />;
};

/**
 * Создает прогресс-бар с указанным процентом выполнения
 * @param progress - процент выполнения (0-100)
 * @param color - цвет прогресс-бара (по умолчанию 'success')
 * @returns React элемент прогресс-бара
 */
export const getProgressBar = (progress: number, color: 'success' | 'warning' | 'danger' = 'success') => {
    return (
        <div className="bg-[#ECF1F4] w-full p-[0.3109vmin] rounded-[0.309vmin] relative flex items-center h-[2.3288vmin]">
            <div
                className={cn(`bg-${color}`, 'rounded-[0.309vmin] h-[1.6937vmin]')}
                style={{ width: `${progress}%` }}
            />
            <span className="absolute left-1/2 translate-x-[-50%] !text-[#002033] h-[1.6937vmin]">{`${progress}%`}</span>
        </div>
    );
};

/**
 * Конфигурация цветов приоритета по ID
 */
const PRIORITY_COLORS = {
    '1': 'bg-[#E7E7E7]',
    '2': 'bg-[#D0A200]', 
    '3': 'bg-[#CE2E00]'
} as const;

/**
 * Определяет цвет фона для приоритета по его ID
 * @param priority - объект приоритета
 * @returns строка с CSS классом цвета фона
 */
const getPriorityBgColorByType = (priority: TBaseObjectDto): string => {
    return PRIORITY_COLORS[priority.id as keyof typeof PRIORITY_COLORS] || 'bg-[#E7E7E7]';
};
